/**
 * Figure de géométrie dans la vidéo : la construction se trace élément par élément, comme au tableau (le point se pose,
 * le segment s'allonge, le cercle se décrit au compas, l'angle se marque), au moment où la voix le nomme.
 * Mêmes calculs que l'app (resolveGeometry) ; dessin en SVG, fonction de l'image courante : identique à l'aperçu.
 */
import { clipLine, FPS, geoLabel, paramsAt, resolveGeometry, type FigureState, type GeoElement, type GeometrySpec, type Vec2 } from "@jange/shared";
import type { ReactElement } from "react";
import type { Theme } from "../styles.js";

const DRAW = Math.round(0.8 * FPS); // durée du tracé d'un élément
const POINT = Math.round(0.35 * FPS);
const STAGGER = Math.round(0.45 * FPS); // éléments nommés au même temps : l'un après l'autre
const clamp01 = (t: number) => Math.max(0, Math.min(1, t));
const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

/** Image de départ du tracé de chaque élément (null : pas encore nommé). */
function startFrames(spec: GeometrySpec, state: FigureState): Map<string, number | null> {
  const out = new Map<string, number | null>();
  const visible = spec.elements.filter((e) => !e.hidden);
  if (state.elements.length === 0) {
    // Construction non découpée par la narration : tout se trace à la suite, dès l'apparition de la figure.
    const t0 = state.drawAt ?? state.shownAt;
    visible.forEach((e, i) => out.set(e.id, t0 === null ? null : t0 + i * STAGGER));
    return out;
  }
  const byAt = new Map<number, number>();
  for (const e of visible) {
    const ev = state.elements.find((x) => x.id === e.id);
    if (!ev) { out.set(e.id, null); continue; }
    const rank = byAt.get(ev.at) ?? 0;
    byAt.set(ev.at, rank + 1);
    out.set(e.id, ev.at + rank * STAGGER);
  }
  return out;
}

export function Geometry({ spec, state, frame, width, height, theme }: { spec: GeometrySpec; state: FigureState; frame: number; width: number; height: number; theme: Theme }) {
  const appear = state.shownAt === null ? 0 : easeOut(clamp01((frame - state.shownAt) / (0.4 * FPS)));
  const params = paramsAt(state, frame);
  const items = resolveGeometry(spec, params);
  const starts = startFrames(spec, state);
  const onWhite = !theme.rough;
  const ink = onWhite ? "#15213B" : theme.text;
  const col: Record<string, string> = { teal: theme.curve, amber: onWhite ? "#E8A317" : theme.amber, ink, muted: onWhite ? "#9AA3B2" : theme.muted };
  const colorOf = (e: GeoElement, d: keyof typeof col) => col[e.color ?? d]!;

  // Repère : même échelle sur les deux axes (les cercles restent ronds), figure centrée.
  const pad = 34;
  const [x0, x1] = spec.view.x, [y0, y1] = spec.view.y;
  const s = Math.min((width - 2 * pad) / (x1 - x0), (height - 2 * pad) / (y1 - y0));
  const ox = (width - s * (x1 - x0)) / 2, oy = (height - s * (y1 - y0)) / 2;
  const P = (p: Vec2): [number, number] => [ox + (p[0] - x0) * s, oy + (y1 - p[1]) * s];
  const sw = theme.rough ? 5 : 4;
  const font = theme.fontFamily;

  const grid: ReactElement[] = [];
  if (spec.grid) {
    for (let x = Math.ceil(x0); x <= x1; x++) { const [a] = P([x, 0]); grid.push(<line key={`gx${x}`} x1={a} y1={oy} x2={a} y2={oy + (y1 - y0) * s} stroke={onWhite ? "#E6E9EF" : "rgba(243,241,232,0.10)"} strokeWidth={1.5} />); }
    for (let y = Math.ceil(y0); y <= y1; y++) { const [, b] = P([0, y]); grid.push(<line key={`gy${y}`} x1={ox} y1={b} x2={ox + (x1 - x0) * s} y2={b} stroke={onWhite ? "#E6E9EF" : "rgba(243,241,232,0.10)"} strokeWidth={1.5} />); }
  }
  if (spec.axes) {
    const [ax, ay] = P([0, 0]);
    grid.push(<line key="ax" x1={ox} y1={ay} x2={ox + (x1 - x0) * s} y2={ay} stroke={ink} strokeWidth={2} opacity={0.6} />, <line key="ay" x1={ax} y1={oy} x2={ax} y2={oy + (y1 - y0) * s} stroke={ink} strokeWidth={2} opacity={0.6} />);
  }

  const nodes: ReactElement[] = [];
  const labels: ReactElement[] = [];
  for (const e of spec.elements) {
    const r = items.get(e.id);
    const t0 = starts.get(e.id);
    if (!r || e.hidden || t0 === null || t0 === undefined || frame < t0) continue;
    const k = easeOut(clamp01((frame - t0) / (r.kind === "point" ? POINT : DRAW)));
    const after = clamp01((frame - t0 - DRAW) / (0.3 * FPS)); // étiquettes, codages, mesures : quand le tracé est fini
    const dash = e.dashed ? `${sw * 3} ${sw * 2.5}` : undefined;
    const label = geoLabel(e);
    switch (r.kind) {
      case "point": {
        const [cx, cy] = P(r.p);
        nodes.push(<circle key={e.id} cx={cx} cy={cy} r={(e.type === "point" && e.draggable ? 9 : 7) * k} fill={e.type === "point" && e.draggable ? col.amber : colorOf(e, "ink")} />);
        if (label) labels.push(<text key={`${e.id}-l`} x={cx + 14} y={cy - 14} fontSize={30} fontWeight={700} fill={ink} fontFamily={font} opacity={clamp01((frame - t0) / (0.4 * FPS))}>{label}</text>);
        break;
      }
      case "line": {
        const ends = clipLine(r.a, r.b, r.extent, spec.view);
        if (!ends) break;
        const [ax, ay] = P(ends[0]), [bx, by] = P(ends[1]);
        const c = colorOf(e, r.extent === "segment" ? "ink" : "teal");
        nodes.push(<line key={e.id} x1={ax} y1={ay} x2={ax + (bx - ax) * k} y2={ay + (by - ay) * k} stroke={c} strokeWidth={sw} strokeLinecap="round" strokeDasharray={dash} />);
        if (e.type === "segment" && e.marks && after > 0) {
          const [mx, my] = [(ax + bx) / 2, (ay + by) / 2], len = Math.hypot(bx - ax, by - ay) || 1, ux = (bx - ax) / len, uy = (by - ay) / len;
          for (let i = 0; i < e.marks; i++) { const off = (i - (e.marks - 1) / 2) * 10; nodes.push(<line key={`${e.id}-m${i}`} x1={mx + ux * off - uy * 12} y1={my + uy * off + ux * 12} x2={mx + ux * off + uy * 12} y2={my + uy * off - ux * 12} stroke={c} strokeWidth={3} opacity={after} />); }
        }
        if (e.type === "segment" && e.showLength && after > 0) {
          const len = Math.hypot(r.b[0] - r.a[0], r.b[1] - r.a[1]);
          const [mx, my] = P([(r.a[0] + r.b[0]) / 2, (r.a[1] + r.b[1]) / 2]);
          labels.push(<text key={`${e.id}-len`} x={mx} y={my + 34} textAnchor="middle" fontSize={26} fill={c} fontFamily={font} opacity={after}>{`${String(Math.round(len * 100) / 100).replace(".", ",")} ${spec.unit}`}</text>);
        }
        if (label && after > 0) labels.push(<text key={`${e.id}-l`} x={ax + (bx - ax) * 0.86} y={ay + (by - ay) * 0.86 - 14} fontSize={28} fontWeight={700} fill={c} fontFamily={font} opacity={after}>{label}</text>);
        break;
      }
      case "circle": {
        const [cx, cy] = P(r.c), rr = r.r * s, C = 2 * Math.PI * rr;
        nodes.push(<circle key={e.id} cx={cx} cy={cy} r={rr} fill="none" stroke={colorOf(e, "teal")} strokeWidth={sw} strokeDasharray={e.dashed ? dash : `${C} ${C}`} strokeDashoffset={e.dashed ? undefined : C * (1 - k)} transform={`rotate(-90 ${cx} ${cy})`} />);
        break;
      }
      case "polygon": {
        const pts = r.pts.map(P);
        const per = pts.reduce((n, p, i) => n + Math.hypot(pts[(i + 1) % pts.length]![0] - p[0], pts[(i + 1) % pts.length]![1] - p[1]), 0);
        const d = `M ${pts.map((p) => p.join(" ")).join(" L ")} Z`;
        const fill = e.type === "polygon" && e.fill ? colorOf(e, "ink") : "none";
        nodes.push(<path key={e.id} d={d} fill={fill} fillOpacity={fill === "none" ? 0 : 0.12 * after} stroke={colorOf(e, "ink")} strokeWidth={sw} strokeLinejoin="round" strokeDasharray={e.dashed ? dash : `${per} ${per}`} strokeDashoffset={e.dashed ? undefined : per * (1 - k)} />);
        break;
      }
      case "angle": {
        const right = e.type === "angle" && !!e.right;
        const rad = Math.min(x1 - x0, y1 - y0) * 0.07;
        const pts: Vec2[] = right
          ? (() => { const u1: Vec2 = [Math.cos(r.start) * rad * 0.8, Math.sin(r.start) * rad * 0.8], u2: Vec2 = [Math.cos(r.start + r.sweep) * rad * 0.8, Math.sin(r.start + r.sweep) * rad * 0.8]; return [[r.v[0] + u1[0], r.v[1] + u1[1]], [r.v[0] + u1[0] + u2[0], r.v[1] + u1[1] + u2[1]], [r.v[0] + u2[0], r.v[1] + u2[1]]] as Vec2[]; })()
          : Array.from({ length: 25 }, (_, i) => { const t = r.start + (r.sweep * i) / 24; return [r.v[0] + Math.cos(t) * rad, r.v[1] + Math.sin(t) * rad] as Vec2; });
        const px = pts.map(P);
        const L = px.reduce((n, p, i) => (i ? n + Math.hypot(p[0] - px[i - 1]![0], p[1] - px[i - 1]![1]) : 0), 0);
        nodes.push(<polyline key={e.id} points={px.map((p) => p.join(",")).join(" ")} fill="none" stroke={colorOf(e, "amber")} strokeWidth={sw - 1} strokeDasharray={`${L} ${L}`} strokeDashoffset={L * (1 - k)} />);
        if (e.type === "angle" && e.showValue && after > 0) {
          const mid = r.start + r.sweep / 2;
          const [tx, ty] = P([r.v[0] + Math.cos(mid) * rad * 2.3, r.v[1] + Math.sin(mid) * rad * 2.3]);
          labels.push(<text key={`${e.id}-v`} x={tx} y={ty + 10} textAnchor="middle" fontSize={28} fontWeight={700} fill={colorOf(e, "amber")} fontFamily={font} opacity={after}>{`${String(Math.round(r.deg * 10) / 10).replace(".", ",")}°`}</text>);
        }
        break;
      }
      case "vector": {
        const [ax, ay] = P(r.a), [bx, by] = P(r.b);
        const ex = ax + (bx - ax) * k, ey = ay + (by - ay) * k, ang = Math.atan2(by - ay, bx - ax), h = 18;
        const c = colorOf(e, "teal");
        nodes.push(<line key={e.id} x1={ax} y1={ay} x2={ex} y2={ey} stroke={c} strokeWidth={sw} strokeLinecap="round" />);
        if (k > 0.9) nodes.push(<polygon key={`${e.id}-h`} points={`${ex},${ey} ${ex - h * Math.cos(ang - 0.45)},${ey - h * Math.sin(ang - 0.45)} ${ex - h * Math.cos(ang + 0.45)},${ey - h * Math.sin(ang + 0.45)}`} fill={c} />);
        break;
      }
      case "text": {
        const [tx, ty] = P(r.p);
        labels.push(<text key={e.id} x={tx} y={ty} fontSize={28} fill={colorOf(e, "ink")} fontFamily={font} opacity={k}>{r.text}</text>);
        break;
      }
    }
  }

  return (
    <div style={{ opacity: appear, transform: `scale(${0.96 + 0.04 * appear})`, background: theme.figureBg, borderRadius: theme.rough ? "255px 15px 225px 15px / 15px 225px 15px 255px" : 22, border: theme.rough ? `3px solid ${theme.cardBorder}` : "none", width, height, boxSizing: "border-box", overflow: "hidden" }}>
      <svg width={width} height={height}>
        <g>{grid}</g>
        <g style={{ filter: theme.rough ? "url(#chalk)" : undefined }}>{nodes}</g>
        <g>{labels}</g>
      </svg>
    </div>
  );
}
